/**
 * Actions for managing recurring transactions
 * Archive, snooze, and interval management operations
 */

import type { Transaction } from '../types';
import {
  getTransactions,
  upsertTransaction,
  dismissRecurringPattern as dismissPattern,
  snoozeRecurringPattern as snoozePattern,
  updateRecurringInterval as saveInterval
} from '../storage';

/**
 * Archive a recurring pattern
 * Removes isRecurring flag from all matching transactions and dismisses the pattern
 *
 * @param pattern - The pattern/display name to archive
 * @returns The pattern key that was dismissed and count of transactions archived
 */
export async function archiveRecurringPattern(pattern: string): Promise<{ patternKey: string; count: number }> {
  console.log('[recurringActions] Archiving pattern:', pattern);

  // Get all transactions with this displayName
  const allTransactions = await getTransactions();
  const matchingTransactions = allTransactions.filter(
    t => t.isRecurring && t.displayName === pattern
  );

  console.log(`[recurringActions] Found ${matchingTransactions.length} transactions to archive`);

  // Remove isRecurring flag from all matching transactions
  for (const transaction of matchingTransactions) {
    await upsertTransaction({
      ...transaction,
      isRecurring: false,
      recurringType: null,
      categorySource: transaction.categorySource === 'rule' ? 'user' : transaction.categorySource
    });
  }

  // Dismiss the pattern so it won't be re-detected
  // Store the original pattern (not normalized) for consistency
  console.log(`[recurringActions] Dismissing pattern:`, pattern);

  await dismissPattern(pattern);

  console.log(`[recurringActions] Archived ${matchingTransactions.length} transactions`);

  return {
    patternKey: pattern,
    count: matchingTransactions.length
  };
}

/**
 * Snooze a recurring pattern for a specified number of days
 *
 * @param pattern - The pattern/display name to snooze
 * @param days - Number of days to snooze (default: 90)
 * @returns The original pattern that was snoozed
 */
export async function snoozeRecurringPattern(pattern: string, days: number = 90): Promise<string> {
  console.log('[recurringActions] Snoozing pattern:', pattern, 'for', days, 'days');

  // Store the original pattern (not normalized) for consistency
  await snoozePattern(pattern, days);

  console.log(`[recurringActions] Snoozed ${pattern} for ${days} days`);

  return pattern;
}

/**
 * Update the interval for a recurring pattern
 *
 * @param pattern - The pattern/display name to update
 * @param avgInterval - New average interval in days
 */
export async function updateRecurringInterval(pattern: string, avgInterval: number): Promise<void> {
  console.log('[recurringActions] Updating interval for:', pattern, 'to', avgInterval, 'days');

  // Store custom interval in database
  await saveInterval(pattern, avgInterval);

  console.log(`[recurringActions] Updated interval for ${pattern} to ${avgInterval} days`);
}
